import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError, apiFetch } from "@/lib/api";
import {
  getAdminMatchErrors,
  regenerateAdminHls,
  regenerateAdminThumbnail,
  retryAdminMatchSlot,
  verifyAdminMatch,
} from "@/lib/api";
import { unscopedKey } from "@/lib/queryClient";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import type { Match } from "@/lib/types";
import { useTranscodeProgress } from "../hooks/useAdminData";

/**
 * M8a B3 + B4 — per-match slot diagnostics panel.
 *
 * Renders inside the expanded library row. One card per slot in
 * `match.video_status`, plus a thumbnail card. Each slot exposes
 * the operator recovery action set:
 *   - Verify (read-only, opens the asset check modal)
 *   - Regen HLS (rebuilds the HLS variant ladder from existing MP4)
 *   - Re-transcode (re-encode from source upload)
 *   - Retry  (error → transcoding)
 *   - Force Re-transcode (ready → transcoding override, with confirm)
 *   - Logs   (opens the errors list for the slot)
 *
 * Force / Re-transcode + Regen-HLS go through `<ConfirmDialog>`
 * because they're destructive to a working slot. Retry on an error
 * slot is a benign action (no confirm).
 *
 * Live transcode progress reads from `useTranscodeProgress` (5 s
 * poll, gated on "any slot transcoding" at the page level).
 */
export function MatchSlotPanel({ match }: { match: Match }) {
  const slots = Object.keys(match.video_status ?? {});
  const progressQuery = useTranscodeProgress({ enabled: true });
  const progress = progressQuery.data ?? {};

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2 text-[0.6rem] font-mono text-ink-muted">
        <span>
          Match ID: <span className="text-ink">{match.id}</span> ·
          updated {match.updated_at || "—"}
        </span>
        <VerifyButton matchId={match.id} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
        {slots.map((slot) => (
          <SlotCard
            key={slot}
            matchId={match.id}
            slot={slot}
            status={match.video_status[slot]}
            progress={progress[`${match.id}/${slot}`]}
          />
        ))}
        <ThumbnailCard match={match} />
      </div>
    </div>
  );
}

function SlotCard({
  matchId,
  slot,
  status,
  progress,
}: {
  matchId: string;
  slot: string;
  status: string;
  progress: { active: boolean; pct?: number; stage?: string; elapsed_s?: number } | undefined;
}) {
  const queryClient = useQueryClient();
  const [confirmKind, setConfirmKind] = useState<
    null | "regen-hls" | "retranscode" | "force"
  >(null);
  const [logsOpen, setLogsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const retry = useMutation({
    mutationFn: (force: boolean) => retryAdminMatchSlot(matchId, slot, force),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: unscopedKey("admin-matches") }),
    onError: (err) => setError(formatErr(err)),
  });
  const regen = useMutation({
    mutationFn: () => regenerateAdminHls(matchId, slot),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: unscopedKey("admin-matches") }),
    onError: (err) => setError(formatErr(err)),
  });

  const isReady = status === "ready";
  const isError = status === "error";
  const isTranscoding = status === "transcoding";

  return (
    <div
      data-testid="admin-slot-card"
      data-slot={slot}
      data-slot-status={status}
      className="flex flex-col gap-2 rounded border border-rule bg-bg-card/60 p-2"
    >
      <header className="flex items-center justify-between">
        <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          {slot.replace("_", " ")}
        </span>
        <span
          data-tone={
            isReady ? "good" : isTranscoding ? "busy" : isError ? "bad" : "idle"
          }
          className={`rounded border px-1.5 py-0.5 text-[0.6rem] font-mono ${
            isReady
              ? "border-good/40 text-good"
              : isTranscoding
                ? "border-accent/40 text-accent"
                : isError
                  ? "border-destructive/40 text-destructive"
                  : "border-rule text-ink-muted"
          }`}
        >
          {status}
        </span>
      </header>

      {isTranscoding && progress ? (
        <p className="text-[0.6rem] font-mono text-ink-muted">
          {progress.stage ?? "encoding"} · {(progress.pct ?? 0).toFixed(0)}%
          {progress.elapsed_s ? ` · ${Math.round(progress.elapsed_s)}s` : ""}
        </p>
      ) : null}

      {error ? (
        <p
          data-testid="admin-slot-error"
          className="text-destructive font-mono text-[0.6rem]"
        >
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-1">
        {isError ? (
          <SlotActionButton
            testid="admin-slot-retry"
            label="Retry"
            onClick={() => {
              setError(null);
              retry.mutate(false);
            }}
            disabled={retry.isPending}
          />
        ) : null}
        {isReady ? (
          <SlotActionButton
            testid="admin-slot-regen-hls"
            label="Regen HLS"
            onClick={() => setConfirmKind("regen-hls")}
            disabled={regen.isPending}
          />
        ) : null}
        {isReady ? (
          <SlotActionButton
            testid="admin-slot-retranscode"
            label="Re-transcode"
            onClick={() => setConfirmKind("retranscode")}
          />
        ) : null}
        {isError ? (
          <SlotActionButton
            testid="admin-slot-force-retranscode"
            label="Force Re-transcode"
            onClick={() => setConfirmKind("force")}
            danger
          />
        ) : null}
        <SlotActionButton
          testid="admin-slot-logs"
          label="Logs"
          onClick={() => setLogsOpen(true)}
        />
      </div>

      {confirmKind ? (
        <ConfirmDialog
          title={
            confirmKind === "regen-hls"
              ? "Regenerate HLS variants?"
              : confirmKind === "force"
                ? "Force re-transcode?"
                : "Re-transcode this slot?"
          }
          message={
            confirmKind === "regen-hls"
              ? "Rebuilds the HLS ladder from the existing MP4. Playback resumes after 1–30 min depending on length."
              : confirmKind === "force"
                ? "Overrides the current state and queues a re-encode from source. Existing HLS will be invalid until done."
                : "Queues a fresh encode from the raw upload."
          }
          confirmLabel="Confirm"
          danger
          onConfirm={() => {
            setError(null);
            if (confirmKind === "regen-hls") {
              regen.mutate();
            } else {
              // Re-review P1: BOTH ready-slot paths (the
              // ready-slot "Re-transcode" button + the error-slot
              // "Force Re-transcode" override) are state overrides
              // — the backend rejects ready → transcoding without
              // `?force=true` ("pass ?force=true to re-transcode
              // a ready slot."). Only the error-slot Retry button
              // calls `retry.mutate(false)` directly (not through
              // this confirm path), since error → transcoding is
              // already a valid transition.
              retry.mutate(true);
            }
            setConfirmKind(null);
          }}
          onClose={() => setConfirmKind(null)}
          data-testid="admin-slot-confirm"
        />
      ) : null}

      {logsOpen ? (
        <SlotLogsModal
          matchId={matchId}
          slot={slot}
          onClose={() => setLogsOpen(false)}
        />
      ) : null}
    </div>
  );
}

function SlotActionButton({
  label,
  onClick,
  disabled,
  danger,
  testid,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  testid: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      data-testid={testid}
      className={`rounded border px-2 py-0.5 text-[0.65rem] font-display uppercase tracking-wider transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
        danger
          ? "border-destructive/40 text-destructive hover:bg-destructive/10"
          : "border-rule text-ink-muted hover:text-ink hover:bg-surface"
      }`}
    >
      {label}
    </button>
  );
}

function ThumbnailCard({ match }: { match: Match }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const regen = useMutation({
    mutationFn: () => regenerateAdminThumbnail(match.id),
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: unscopedKey("admin-matches") }),
    onError: (err) => setError(formatErr(err)),
  });

  return (
    <div
      data-testid="admin-thumbnail-card"
      className="flex flex-col gap-2 rounded border border-rule bg-bg-card/60 p-2"
    >
      <header className="flex items-center justify-between">
        <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
          Thumbnail
        </span>
        <span
          className={`rounded border px-1.5 py-0.5 text-[0.6rem] font-mono ${
            match.has_thumbnail
              ? "border-good/40 text-good"
              : "border-rule text-ink-muted"
          }`}
        >
          {match.has_thumbnail ? "present" : "missing"}
        </span>
      </header>
      {error ? (
        <p className="text-destructive font-mono text-[0.6rem]">{error}</p>
      ) : null}
      <button
        type="button"
        onClick={() => {
          setError(null);
          regen.mutate();
        }}
        disabled={regen.isPending}
        data-testid="admin-thumbnail-regenerate"
        className="rounded border border-rule text-ink-muted hover:text-ink hover:bg-surface px-2 py-0.5 text-[0.65rem] font-display uppercase tracking-wider transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Regenerate
      </button>
    </div>
  );
}

function VerifyButton({ matchId }: { matchId: string }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="admin-matches-verify"
        className="rounded border border-rule text-ink-muted hover:text-ink hover:bg-surface px-2 py-0.5 text-[0.65rem] font-display uppercase tracking-wider transition-colors"
      >
        Verify
      </button>
      {open ? (
        <VerifyAssetsModal matchId={matchId} onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
}

function VerifyAssetsModal({
  matchId,
  onClose,
}: {
  matchId: string;
  onClose: () => void;
}) {
  const query = useQuery({
    queryKey: unscopedKey("admin-verify", { matchId }),
    queryFn: () => verifyAdminMatch(matchId),
  });

  return (
    <ConfirmDialog
      title="Asset verification"
      confirmLabel="Close"
      message={
        query.isLoading ? (
          <p className="text-ink-muted font-mono text-xs">Checking…</p>
        ) : query.error ? (
          <p className="text-destructive font-mono text-xs">
            {formatErr(query.error)}
          </p>
        ) : query.data ? (
          <div className="flex flex-col gap-2 font-mono text-xs">
            <p>
              Thumbnail:{" "}
              <span className={query.data.thumbnail ? "text-good" : "text-destructive"}>
                {query.data.thumbnail ? "OK" : "MISSING"}
              </span>
            </p>
            {Object.entries(query.data.slots ?? {}).map(([slot, s]) => (
              <p key={slot}>
                {slot}: MP4{" "}
                <span className={s.mp4 ? "text-good" : "text-destructive"}>
                  {s.mp4 ? "OK" : "MISSING"}
                </span>{" "}
                · HLS{" "}
                <span className={s.hls ? "text-good" : "text-destructive"}>
                  {s.hls ? "OK" : "MISSING"}
                </span>
              </p>
            ))}
            {(query.data.issues ?? []).length > 0 ? (
              <ul className="text-destructive list-disc pl-4">
                {query.data.issues.map((issue, i) => (
                  <li key={i}>{issue}</li>
                ))}
              </ul>
            ) : null}
          </div>
        ) : null
      }
      onConfirm={onClose}
      onClose={onClose}
      data-testid="admin-matches-verify-result"
    />
  );
}

function SlotLogsModal({
  matchId,
  slot,
  onClose,
}: {
  matchId: string;
  slot: string;
  onClose: () => void;
}) {
  const query = useQuery({
    queryKey: unscopedKey("admin-match-errors", { matchId, slot }),
    queryFn: () => getAdminMatchErrors(matchId),
  });
  const errors = (query.data?.errors ?? []).filter(
    (e: Record<string, unknown>) => e.slot === slot || !e.slot,
  );

  return (
    <ConfirmDialog
      title={`Logs — ${slot}`}
      confirmLabel="Close"
      message={
        query.isLoading ? (
          <p className="text-ink-muted font-mono text-xs">Loading…</p>
        ) : errors.length === 0 ? (
          <p className="text-ink-muted font-mono text-xs">No errors recorded for this slot.</p>
        ) : (
          <ul className="flex flex-col gap-1.5 font-mono text-xs max-h-96 overflow-auto">
            {errors.map((e: Record<string, unknown>, i: number) => (
              <li
                key={i}
                className="rounded border border-rule/60 bg-surface/40 px-2 py-1.5"
              >
                <div className="text-ink-muted">{String(e.timestamp ?? "")}</div>
                <div className="text-ink">
                  <span className="text-destructive">{String(e.error_code ?? "error")}</span>{" "}
                  {String(e.reason ?? "")}
                </div>
                {e.details ? (
                  <pre className="mt-1 text-[0.6rem] text-ink-muted whitespace-pre-wrap">
                    {typeof e.details === "string"
                      ? e.details
                      : JSON.stringify(e.details, null, 2)}
                  </pre>
                ) : null}
              </li>
            ))}
          </ul>
        )
      }
      onConfirm={onClose}
      onClose={onClose}
      data-testid="admin-slot-logs"
    />
  );
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    return typeof err.detail === "string" ? err.detail : `HTTP ${err.status}`;
  }
  return "Request failed";
}

// `apiFetch` re-imported so the file is self-contained for the
// fetch wrappers used in mutations.
void apiFetch;
